const getApiBase = () => {
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  if (base.endsWith("/admin")) {
    return `${base}/games`;
  }
  return `${base}/admin/games`;
};

const getAuthHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("token");
  const headers = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  // A multipart body must set its own Content-Type, boundary and all, so
  // setting it here would corrupt the upload.
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
};

const handleResponse = async (response) => {
  // Read the body once as text, so a response that is not JSON can be reported
  // as such rather than being quietly turned into {} — the same failure mode
  // taskApi.js documents, where a missing route surfaced three layers away.
  const raw = await response.text();
  let data = {};
  let isJson = true;

  if (raw.trim()) {
    try {
      data = JSON.parse(raw);
    } catch {
      isJson = false;
    }
  }

  if (!response.ok || data?.status === false) {
    const errorMsg = data?.message || data?.error || `Request failed with status ${response.status}`;
    const err = new Error(errorMsg);
    err.response = data;
    err.status = response.status;
    throw err;
  }

  if (!isJson) {
    const err = new Error(
      `The server returned a non-JSON response (HTTP ${response.status}). This API route may be missing from the deployed backend.`
    );
    err.response = raw.slice(0, 200);
    throw err;
  }

  return data;
};

/**
 * Turn a game form into a request body.
 *
 * An icon file forces multipart; without one a plain JSON body is sent, which
 * keeps a simple toggle from having to build a FormData. An empty per-event
 * limit means "unlimited" and is sent as null rather than dropped, so clearing
 * it actually clears it. The per-day limit is required and sent as it is.
 */
const buildGameBody = (form) => {
  const fields = {
    name: form.name,
    description: form.description ?? "",
    route: form.route ?? "",
    rewardInfo: form.rewardInfo ?? "",
    gameTime: form.gameTime,
    minPoints: form.minPoints,
    maxPoints: form.maxPoints,
    cooldownSeconds: form.cooldownSeconds,
    maxPlaysPerEvent: form.maxPlaysPerEvent === "" || form.maxPlaysPerEvent == null ? null : form.maxPlaysPerEvent,
    // Required, never null: the app reads it as `total_play`.
    maxPlaysPerDay: form.maxPlaysPerDay,
    sortOrder: form.sortOrder ?? 0,
    isActive: Boolean(form.isActive),
  };

  // The game-specific block. Sent as a string so a multipart form can carry it
  // unchanged; the backend parses and validates it either way.
  if (form.config !== undefined) {
    fields.config = form.config === null || form.config === "" ? null : form.config;
  }

  if (form.gameKey) fields.gameKey = form.gameKey;
  if (form.removeIcon) fields.removeIcon = true;
  if (form.removeBanner) fields.removeBanner = true;

  if (!form.iconFile && !form.bannerFile) {
    return { body: JSON.stringify(fields), isMultipart: false };
  }

  const fd = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    // FormData stringifies null as "null"; the backend reads that as an
    // explicit "clear this field", which is exactly what null means here.
    // An object would become "[object Object]", so config is serialised first.
    if (value !== null && typeof value === "object") {
      fd.append(key, JSON.stringify(value));
      return;
    }
    fd.append(key, value === null ? "null" : value);
  });
  if (form.iconFile) fd.append("icon", form.iconFile);
  if (form.bannerFile) fd.append("banner", form.bannerFile);

  return { body: fd, isMultipart: true };
};

// ==========================================
// Games catalog (see px's src/admin/game module)
// ==========================================

export const fetchGames = async ({ page = 1, limit = 20, search = "", status = "" } = {}) => {
  const query = new URLSearchParams();
  query.append("page", page);
  query.append("limit", limit);
  if (search) query.append("search", search.trim());
  if (status && status !== "all") query.append("status", status);

  const res = await fetch(`${getApiBase()}/list?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const fetchGame = async (id) => {
  const res = await fetch(`${getApiBase()}/view/${id}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const createGame = async (form) => {
  const { body, isMultipart } = buildGameBody(form);
  const res = await fetch(`${getApiBase()}/create`, {
    method: "POST",
    headers: getAuthHeaders(isMultipart),
    body,
  });
  return handleResponse(res);
};

export const updateGame = async (id, form) => {
  const { body, isMultipart } = buildGameBody(form);
  const res = await fetch(`${getApiBase()}/edit/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(isMultipart),
    body,
  });
  return handleResponse(res);
};

export const deleteGame = async (id) => {
  const res = await fetch(`${getApiBase()}/delete/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const setGameActive = async (id, isActive) => {
  const res = await fetch(`${getApiBase()}/${isActive ? "activate" : "deactivate"}/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

// ==========================================
// Event-wise reporting
//
// Every call below takes an `eventId`. Omitted, the backend answers for the
// event running now; "all" opts out of event scoping entirely.
// ==========================================

export const fetchGameEvents = async () => {
  const res = await fetch(`${getApiBase()}/events`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const fetchGameSummary = async ({ eventId = "" } = {}) => {
  const query = new URLSearchParams();
  if (eventId !== "" && eventId != null) query.append("eventId", eventId);

  const res = await fetch(`${getApiBase()}/summary?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const fetchGameHistory = async ({
  eventId = "",
  gameId = "",
  page = 1,
  limit = 20,
  search = "",
  status = "",
  result = "",
} = {}) => {
  const query = new URLSearchParams();
  query.append("page", page);
  query.append("limit", limit);
  if (eventId !== "" && eventId != null) query.append("eventId", eventId);
  if (gameId !== "" && gameId != null && gameId !== "all") query.append("gameId", gameId);
  if (search) query.append("search", search.trim());
  if (status && status !== "all") query.append("status", status);
  if (result && result !== "all") query.append("result", result);

  const res = await fetch(`${getApiBase()}/history?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const fetchGamePlayers = async ({ eventId = "", gameId = "", page = 1, limit = 20, search = "" } = {}) => {
  const query = new URLSearchParams();
  query.append("page", page);
  query.append("limit", limit);
  if (eventId !== "" && eventId != null) query.append("eventId", eventId);
  if (gameId !== "" && gameId != null && gameId !== "all") query.append("gameId", gameId);
  if (search) query.append("search", search.trim());

  const res = await fetch(`${getApiBase()}/players?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};
